<template>
  <div>
    <div>
      <BotEditorSubHeader
        :title="$t('botEditor.prompting.title')"
        :subtitle="$t('botEditor.prompting.subtitle')"
      />
    </div>

    <div v-if="loading" class="text-center text-gray-400 py-12">
      {{ $t('botEditor.prompting.loadingText') }}
    </div>

    <div v-else class="prompts-grid">
      <!-- System Prompt (unified) -->
      <div class="prompt-card">
        <div class="card-header">
          <div class="icon-wrapper rounded-lg">
            <span class="material-icons !text-3xl">auto_fix_high</span>
          </div>
          <div>
            <h3 class="text-xl font-semibold">{{ $t('botEditor.prompting.systemPromptTitle') }}</h3>
            <p class="text-sm text-gray-400 mt-1">
              {{ $t('botEditor.prompting.systemPromptDescription') }}
            </p>
          </div>
        </div>
        
        <div class="card-body">
          <label for="input-system-prompt" class="block text-sm font-medium text-gray-300 mb-2">
            {{ $t('botEditor.prompting.systemPromptLabel') }}
          </label>
          <!-- Mono face from the global input-mono class; size="small" (14px) is the
               closest field size to the former 13px. Vertical resize comes from preflight. -->
          <Textarea
            id="input-system-prompt"
            v-model="prompts.systemPrompt"
            rows="12"
            size="small"
            class="input-mono w-full"
            :placeholder="$t('botEditor.prompting.systemPromptPlaceholder')"
          />
          <p class="text-xs text-gray-500 mt-2 flex items-center">
            <span class="material-icons !text-sm mr-1">info</span>
            {{ $t('botEditor.prompting.systemPromptInfo') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div v-if="!loading" class="action-buttons">
      <Button id="btn-request-reset-prompt" severity="secondary" :disabled="saving" @click="requestReset">
        <span class="material-icons">undo</span>
        {{ $t('botEditor.prompting.resetButton') }}
      </Button>
      <Button
        id="btn-save-prompt"
        severity="primary"
        :label="saving ? $t('botEditor.prompting.savingButton') : $t('botEditor.prompting.saveButton')"
        :loading="saving"
        :disabled="saving || !hasChanges"
        @click="savePrompts"
      >
        <template #icon>
          <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">save</span>
        </template>
      </Button>
    </div>

    <!-- Reset confirmation goes through the global <ConfirmDialog /> (App.vue) via useConfirm() -->
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import api from '../../services/api';
import BotEditorSubHeader from '../../components/ui/BotEditorSubHeader.vue';

export default {
  name: 'Prompting',

  setup() {
    const { t } = useI18n();
    const toast = useToast();
    const confirm = useConfirm();
    const route = useRoute();
    const botId = route.params.id;
    const workspaceId = route.params.workspaceId;

    const loading = ref(true);
    const saving = ref(false);
    const prompts = ref({
      systemPrompt: ''
    });
    const originalPrompts = ref({});

    const hasChanges = computed(() => {
      return prompts.value.systemPrompt !== originalPrompts.value.systemPrompt;
    });

    const loadPrompts = async () => {
      try {
        loading.value = true;
        const response = await api.get(`/workspaces/${workspaceId}/bots/${botId}/prompts`);
        prompts.value = {
          systemPrompt: response.data.data.systemPrompt
        };
        originalPrompts.value = { ...prompts.value };
      } catch (error) {
        Console.error('Error loading prompts:', error);
        toast.add({ severity: 'error', summary: t('botEditor.prompting.errorLoading'), life: 3000 });
      } finally {
        loading.value = false;
      }
    };

    const savePrompts = async () => {
      if (!prompts.value.systemPrompt) {
        toast.add({ severity: 'warn', summary: t('botEditor.prompting.errorRequired'), life: 3000 });
        return;
      }

      if (prompts.value.systemPrompt.length < 10) {
        toast.add({ severity: 'warn', summary: t('botEditor.prompting.errorMinLength'), life: 3000 });
        return;
      }

      try {
        saving.value = true;
        await api.put(`/workspaces/${workspaceId}/bots/${botId}/prompts`, {
          systemPrompt: prompts.value.systemPrompt
        });
        originalPrompts.value = { ...prompts.value };
        toast.add({ severity: 'success', summary: t('botEditor.prompting.successSave'), life: 3000 });
      } catch (error) {
        Console.error('Error saving prompts:', error);
        toast.add({ severity: 'error', summary: t('botEditor.prompting.errorSaving'), life: 3000 });
      } finally {
        saving.value = false;
      }
    };

    const confirmReset = async () => {
      try {
        saving.value = true;
        const response = await api.post(`/workspaces/${workspaceId}/bots/${botId}/prompts/reset`);
        prompts.value = {
          systemPrompt: response.data.data.systemPrompt
        };
        originalPrompts.value = { ...prompts.value };
        toast.add({ severity: 'success', summary: t('botEditor.prompting.successReset'), life: 3000 });
      } catch (error) {
        Console.error('Error resetting prompts:', error);
        toast.add({ severity: 'error', summary: t('botEditor.prompting.errorReset'), life: 3000 });
      } finally {
        saving.value = false;
      }
    };

    // The dialog closes on accept and the request's progress shows on the Save button
    // (saving), so the global ConfirmDialog (App.vue) fits; no ConfirmActionDialog needed.
    // Message and warning share the single message line ConfirmDialog renders.
    const requestReset = () => {
      confirm.require({
        header: t('botEditor.prompting.resetModalTitle'),
        message: `${t('botEditor.prompting.resetModalMessage')} ${t('botEditor.prompting.resetModalWarning')}`,
        icon: 'warning',
        acceptLabel: t('botEditor.prompting.resetModalConfirm'),
        rejectLabel: t('botEditor.prompting.resetModalCancel'),
        acceptProps: { id: 'btn-confirm-reset-prompt', severity: 'warn' },
        rejectProps: { id: 'btn-cancel-reset-prompt', severity: 'secondary' },
        accept: confirmReset,
      });
    };

    onMounted(() => {
      loadPrompts();
    });

    return {
      loading,
      saving,
      prompts,
      hasChanges,
      savePrompts,
      requestReset
    };
  }
};
</script>

<style scoped>
.prompts-grid {
  display: grid;
  gap: 24px;
  margin-bottom: 32px;
}

.prompt-card {
  background: var(--p-surface-800); /* Surface Raised (D17): the gray-700 Textarea reads as a recessed well */
  border: 2px solid #4b5563;
  border-radius: 12px;
  padding: 24px;
  transition: all 0.2s;
}

.prompt-card:hover {
  border-color: #6b7280;
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #4b5563;
}

.icon-wrapper {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.card-body {
  margin-top: 16px;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 24px;
  border-top: 1px solid #4b5563;
}
</style>
